import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Mail } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF, WA_HREF, EMAIL_HREF, WhatsAppIcon, scrollToId } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const SECTION_IDS = ["services", "fleet", "destinations", "booking", "contact"];

const LangToggle = ({ compact = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div data-testid="lang-toggle" className={`flex items-center rounded-full border border-[#C5A059]/40 p-0.5 ${compact ? "" : "mr-1"}`}>
      {["en", "de"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`px-2.5 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase transition-colors duration-300 ${
            lang === l ? "bg-[#C5A059] text-[#141312]" : "text-stone-400 hover:text-[#C5A059]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { lang } = useLang();
  const t = T[lang].nav;

  const links = SECTION_IDS.map((id) => [t[id], id]);

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#1A1613]/85 backdrop-blur-md border-b border-amber-100/10"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between gap-4">
        <button
          data-testid="nav-logo-button"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="flex items-center gap-3 shrink-0"
        >
          <span className="bg-black rounded-xl p-1.5 shadow-md shadow-black/40 ring-1 ring-[#C5A059]/40">
            <img src="/images/logo.webp" alt="Kumar Taxi & Tours logo" className="h-9 w-9 rounded-lg object-cover" />
          </span>
          <span className="text-left leading-tight hidden sm:block">
            <span className="block font-serif-display font-semibold text-lg tracking-wide text-stone-100">KUMAR TAXI</span>
            <span className="block font-mono-label text-[10px] tracking-[0.3em] text-[#C5A059]">&amp; TOURS · SALZBURG</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {links.map(([label, id]) => (
            <button
              key={id}
              data-testid={`nav-link-${id}`}
              onClick={() => scrollToId(id)}
              className="text-sm font-medium text-stone-400 hover:text-[#C5A059] transition-colors duration-300 tracking-wide"
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2.5">
          <LangToggle />
          <a
            data-testid="nav-call-button"
            href={PHONE_HREF}
            className="flex items-center gap-2 rounded-full bg-[#C5A059] px-4 py-2 text-sm font-semibold text-[#141312] hover:bg-[#D9B877] transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            <span className="hidden xl:inline">{PHONE_DISPLAY}</span>
          </a>
          <a
            data-testid="nav-whatsapp-button"
            href={WA_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1DA851] transition-colors duration-300"
          >
            <WhatsAppIcon className="w-4 h-4" />
            {t.whatsapp}
          </a>
          <a
            data-testid="nav-email-button"
            href={EMAIL_HREF}
            className="flex items-center gap-2 rounded-full bg-[#D64545] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B93737] transition-colors duration-300"
          >
            <Mail className="w-4 h-4" />
            E-Mail
          </a>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <LangToggle compact />
          <button
            data-testid="nav-mobile-menu-button"
            className="lg:hidden p-2 text-stone-100"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden bg-[#1A1613]/95 backdrop-blur-md border-t border-amber-100/10"
          >
            <div className="px-6 py-5 flex flex-col gap-4">
              {links.map(([label, id]) => (
                <button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  onClick={() => { setOpen(false); scrollToId(id); }}
                  className="text-left font-serif-display text-2xl text-stone-100"
                >
                  {label}
                </button>
              ))}
              <div className="grid grid-cols-3 gap-3 pt-2">
                <a data-testid="nav-mobile-call-button" href={PHONE_HREF} className="flex items-center justify-center gap-2 rounded-full bg-[#C5A059] px-3 py-3 text-sm font-semibold text-[#141312]">
                  <Phone className="w-4 h-4" /> {t.call}
                </a>
                <a data-testid="nav-mobile-whatsapp-button" href={WA_HREF} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-3 py-3 text-sm font-semibold text-white">
                  <WhatsAppIcon className="w-4 h-4" /> {t.whatsapp}
                </a>
                <a data-testid="nav-mobile-email-button" href={EMAIL_HREF} className="flex items-center justify-center gap-2 rounded-full bg-[#D64545] px-3 py-3 text-sm font-semibold text-white">
                  <Mail className="w-4 h-4" /> E-Mail
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
